import { useCallback, useState } from "react";
import { addDays, format } from "date-fns";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../utils/hooks/useFacility";
import { GET_EXERCISE_BY_DAY } from "../../../api/apiRoutes";
import { Exercise } from "../../../models/Exercise";
import { TicketAccess, setTicketAccessFromExercise } from "../../../models/TicketAccess";
import { KARNET_MAX_HORIZON_WEEKS } from "../../CourtGrid/components/CourtReservationModal/CourtTicketSelection/karnetConcept";
import { KarnetTerm } from "../../CourtGrid/components/CourtReservationModal/CourtTicketSelection/useKarnetSchedule";

interface UseCalendarKarnetScheduleProps {
  exerciseDetails: Exercise | null;
  anchorTicketAccess: TicketAccess;
}

const WEEK_REQUEST_TIMEOUT_MS = 8000;

// Zabezpieczenie przed zawieszonym requestem (serwer nie odpowiada) — bez tego jeden
// "wiszący" fetch blokowałby całą pętlę w nieskończoność i loader nigdy by nie zniknął.
function withTimeout(promise: Promise<any>, ms: number): Promise<any> {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve({ isError: true, errorTable: ["timeout"] }), ms)),
  ]);
}

/**
 * Odpowiednik useKarnetSchedule z CourtGrid, ale dla sprzedaży biletów wejściowych
 * (Exercise/CalendarSale) zamiast grafiku kortów. Nie ma tu jednego bulk-endpointu
 * zwracającego wszystkie przyszłe wystąpienia danego zajęcia — attractions/byDates
 * (GET_AVAILABLE_EXERCISE) zwraca tylko jedno/reprezentatywne wystąpienie na nazwę,
 * więc kolejne tygodnie sprawdzamy pojedynczo przez GET_EXERCISE_BY_DAY (ten sam
 * endpoint co CalendarDayPicker), dopasowując po godzinie startu.
 */
export default function useCalendarKarnetSchedule({
  exerciseDetails,
  anchorTicketAccess,
}: UseCalendarKarnetScheduleProps) {
  const { get } = useApiRequest();
  const facilityIds = useFacility();

  const [terms, setTerms] = useState<KarnetTerm[]>([]);
  const [loading, setLoading] = useState(false);

  const horizonCount = terms.length;
  const lastAvailableDateLabel = terms.length > 0 ? terms[terms.length - 1].date : "";

  const fetchHorizon = useCallback(
    async (requiredCapacity: number) => {
      if (!exerciseDetails) return;

      setLoading(true);

      try {
        const anchorDate = new Date(exerciseDetails.startTime);
        const startTimeLabel = format(anchorDate, "HH:mm");

        const computedTerms: KarnetTerm[] = [
          {
            index: 1,
            date: format(anchorDate, "yyyy-MM-dd"),
            startTimeLabel,
            status: "available",
            ticketAccess: anchorTicketAccess,
          },
        ];

        // Requesty po kolei, nie równolegle — backend nie radzi sobie poprawnie z paczką
        // 25 jednoczesnych zapytań o ten sam obiekt (obserwowane błędne/gubione wyniki).
        for (let week = 1; week < KARNET_MAX_HORIZON_WEEKS; week++) {
          const dateLabel = format(addDays(anchorDate, 7 * week), "yyyy-MM-dd");

          const { result, isError } = await withTimeout(
            get(GET_EXERCISE_BY_DAY(facilityIds, dateLabel)),
            WEEK_REQUEST_TIMEOUT_MS,
          );
          const dayExercises: Exercise[] = !isError && Array.isArray(result) ? result : [];

          const matching = dayExercises.find(
            (ex) =>
              ex.exerciseGroup?.objectId === exerciseDetails.exerciseGroup?.objectId &&
              format(new Date(ex.startTime), "HH:mm") === startTimeLabel,
          );

          // Brak zajęć tej samej grupy o tej samej godzinie w danym tygodniu = grafik nie
          // sięga tak daleko albo cykl się urwał. Tu kończy się horyzont.
          if (!matching) break;

          const status: KarnetTerm["status"] =
            matching.capacity - (matching.reserved + matching.prereservation) >= requiredCapacity
              ? "available"
              : "sold-out";

          computedTerms.push({
            index: week + 1,
            date: dateLabel,
            startTimeLabel,
            status,
            ticketAccess: setTicketAccessFromExercise(matching.objectId, matching.name, matching.startTime),
          });
        }

        setTerms(computedTerms);
      } finally {
        setLoading(false);
      }
    },
    [exerciseDetails, facilityIds, get, anchorTicketAccess],
  );

  const reset = useCallback(() => {
    setTerms([]);
  }, []);

  return { terms, horizonCount, lastAvailableDateLabel, loading, fetchHorizon, reset };
}
